(() => {
  if (window.AlphaPCEnterpriseAIReady) return;
  window.AlphaPCEnterpriseAIReady = true;

  const ATTRIBUTION_KEYS = [
    'gclid',
    'gbraid',
    'wbraid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  const safeStorage = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (error) {
        return;
      }
    },
    remove(storage, key) {
      try {
        storage.removeItem(key);
      } catch (error) {
        return;
      }
    }
  };

  function pushEvent(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page_path: window.location.pathname,
      ts: Date.now(),
      ...payload
    });
  }

  function makeLeadId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `eai-${window.crypto.randomUUID()}`;
    }
    return `eai-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  function fieldValue(form, selector) {
    const field = form.querySelector(selector);
    return field ? String(field.value || '').trim() : '';
  }

  function formName(form) {
    return fieldValue(form, 'input[name="contact[Form]"]') || 'Enterprise AI Configuration Request';
  }

  function setHidden(root, key, value) {
    const field = root.querySelector(`[data-eai-hidden="${key}"]`);
    if (field) field.value = value || '';
  }

  function captureFormValues(form) {
    const values = {};
    const data = new FormData(form);
    for (const [key, value] of data.entries()) {
      if (typeof value !== 'string') continue;
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        values[key] = `${values[key]}, ${value}`;
      } else {
        values[key] = value;
      }
    }
    return values;
  }

  function restoreFormValues(form, values) {
    if (!values) return;
    Object.entries(values).forEach(([name, value]) => {
      const fields = form.querySelectorAll(`[name="${CSS.escape(name)}"]`);
      fields.forEach((field) => {
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = String(value).split(', ').includes(field.value);
        } else if (field.type !== 'hidden' || name === 'contact[Lead ID]') {
          field.value = value;
        }
      });
    });
  }

  function isQualifiedBudget(value) {
    return Boolean(value && value !== 'under_50k');
  }

  function initAttribution(root, form) {
    window.AlphaPCLeadAttribution?.applyToForm(form);

    const params = new URLSearchParams(window.location.search);
    ATTRIBUTION_KEYS.forEach((key) => {
      const queryValue = params.get(key);
      if (queryValue) safeStorage.set(window.localStorage, `eai_attr_${key}`, queryValue);
      const stored = queryValue || safeStorage.get(window.localStorage, `eai_attr_${key}`) || '';
      setHidden(root, key, stored);
    });

    const leadId = fieldValue(form, '[data-eai-hidden="lead_id"]') || makeLeadId();
    setHidden(root, 'lead_id', leadId);
    setHidden(root, 'landing_page', window.location.href.split('#')[0]);
  }

  function updateBudgetBranch(root, form) {
    const budget = form.querySelector('[data-eai-budget]');
    const note = root.querySelector('[data-eai-budget-note]');
    const leadType = root.querySelector('[data-eai-lead-type]');
    if (!budget) return;

    const underFloor = budget.value === 'under_50k';
    if (note) note.classList.toggle('is-visible', underFloor);
    if (leadType) leadType.value = underFloor ? 'under_50k_project' : 'enterprise_ai_project';
  }

  function setGroupVisibility(group, visible) {
    if (!group) return;
    group.hidden = !visible;
    group.querySelectorAll('input, select, textarea').forEach((field) => {
      field.disabled = !visible;
    });
  }

  function updateConditionalGroups(form) {
    const projectType = fieldValue(form, '[data-eai-project-type]');
    const country = fieldValue(form, '[data-eai-country]');
    const deploymentVisible = projectType === 'rackmount_server' || projectType === 'multi_node';
    const exportVisible = Boolean(country && country !== 'Canada' && country !== 'United States');
    setGroupVisibility(form.querySelector('[data-eai-deployment-group]'), deploymentVisible);
    setGroupVisibility(form.querySelector('[data-eai-export-group]'), exportVisible);
  }

  function hasAdvancedValues(values) {
    if (!values) return false;
    const coreNames = new Set([
      'contact[Form]',
      'contact[Lead Type]',
      'contact[Lead ID]',
      'contact[GCLID]',
      'contact[GBRAID]',
      'contact[WBRAID]',
      'contact[UTM Source]',
      'contact[UTM Medium]',
      'contact[UTM Campaign]',
      'contact[UTM Term]',
      'contact[UTM Content]',
      'contact[Canonical Landing Page]',
      'contact[name]',
      'contact[email]',
      'contact[Company]',
      'contact[Delivery Country]',
      'contact[Project Type]',
      'contact[Budget Band]',
      'contact[Purchase Timing]',
      'contact[phone]',
      'contact[body]',
      'form_type',
      'utf8'
    ]);
    return Object.entries(values).some(([name, value]) => !coreNames.has(name) && String(value).trim());
  }

  function savePendingSubmission(root, form, startedAt) {
    const leadId = fieldValue(form, '[data-eai-hidden="lead_id"]') || makeLeadId();
    setHidden(root, 'lead_id', leadId);
    const advanced = form.querySelector('[data-eai-advanced]');
    const pending = {
      leadId,
      budgetBand: fieldValue(form, '[data-eai-budget]'),
      currency: fieldValue(form, '[data-eai-currency]'),
      country: fieldValue(form, '[data-eai-country]'),
      projectType: fieldValue(form, '[data-eai-project-type]'),
      oem: fieldValue(form, '[data-eai-oem]'),
      accelerator: fieldValue(form, '[data-eai-accelerator]'),
      systemQuantity: fieldValue(form, '[data-eai-system-quantity]'),
      optionalDetails: Boolean(advanced?.open),
      completionSeconds: startedAt ? Math.max(1, Math.round((performance.now() - startedAt) / 1000)) : null,
      values: captureFormValues(form),
      createdAt: new Date().toISOString()
    };
    safeStorage.set(window.sessionStorage, 'eai_pending_enterprise_quote', JSON.stringify(pending));
    return pending;
  }

  function readPendingSubmission() {
    const raw = safeStorage.get(window.sessionStorage, 'eai_pending_enterprise_quote');
    try {
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function handleServerResult(root, form) {
    const pending = readPendingSubmission();
    const success = root.querySelector('[data-eai-success]');
    const errors = root.querySelector('[data-eai-errors]');

    if (errors && pending?.values) {
      restoreFormValues(form, pending.values);
      updateBudgetBranch(root, form);
      updateConditionalGroups(form);
      const advanced = form.querySelector('[data-eai-advanced]');
      if (advanced && hasAdvancedValues(pending.values)) advanced.open = true;
      errors.focus({ preventScroll: true });
    }

    if (!success || !pending?.leadId) return;
    const sentKey = `eai_event_sent_${pending.leadId}`;
    if (!safeStorage.get(window.sessionStorage, sentKey)) {
      const qualified = isQualifiedBudget(pending.budgetBand);
      pushEvent({
        event: qualified ? 'enterprise_quote_submitted' : 'under_50k_lead_submitted',
        form_name: formName(form),
        form_id: form.id,
        lead_type: qualified ? 'enterprise_ai_project' : 'under_50k_project',
        lead_id: pending.leadId,
        transaction_id: pending.leadId,
        budget_band: pending.budgetBand,
        budget_currency: pending.currency,
        delivery_country: pending.country,
        project_type: pending.projectType,
        preferred_oem: pending.oem,
        accelerator: pending.accelerator,
        system_quantity: pending.systemQuantity,
        optional_details_opened: pending.optionalDetails,
        completion_seconds: pending.completionSeconds,
        section_id: root.dataset.eaiSection || ''
      });
      safeStorage.set(window.sessionStorage, sentKey, '1');
    }

    safeStorage.remove(window.sessionStorage, 'eai_pending_enterprise_quote');
    success.focus({ preventScroll: true });
    success.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function initFormViewTracking(root, form) {
    let viewed = false;
    const recordView = () => {
      if (viewed) return;
      viewed = true;
      pushEvent({
        event: 'enterprise_form_view',
        form_name: formName(form),
        form_id: form.id,
        section_id: root.dataset.eaiSection || ''
      });
    };
    if (!('IntersectionObserver' in window)) {
      recordView();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        recordView();
        observer.disconnect();
      }
    }, { threshold: 0.25 });
    observer.observe(form);
  }

  function initSection(root) {
    const form = root.querySelector('.eai-form');
    if (!form) return;
    if (root.dataset.eaiFormInitialized === 'true') return;
    root.dataset.eaiFormInitialized = 'true';

    let startedAt = null;
    let detailsTracked = false;
    initAttribution(root, form);
    updateBudgetBranch(root, form);
    updateConditionalGroups(form);
    initFormViewTracking(root, form);
    const budget = form.querySelector('[data-eai-budget]');
    const projectType = form.querySelector('[data-eai-project-type]');
    const country = form.querySelector('[data-eai-country]');
    const advanced = form.querySelector('[data-eai-advanced]');

    budget?.addEventListener('change', () => updateBudgetBranch(root, form));
    projectType?.addEventListener('change', () => updateConditionalGroups(form));
    country?.addEventListener('change', () => updateConditionalGroups(form));

    form.addEventListener('focusin', () => {
      if (startedAt !== null) return;
      startedAt = performance.now();
      pushEvent({
        event: 'enterprise_form_started',
        form_name: formName(form),
        form_id: form.id,
        section_id: root.dataset.eaiSection || ''
      });
    });

    advanced?.addEventListener('toggle', () => {
      if (!advanced.open || detailsTracked) return;
      detailsTracked = true;
      pushEvent({
        event: 'enterprise_form_details_opened',
        form_name: formName(form),
        form_id: form.id,
        section_id: root.dataset.eaiSection || ''
      });
    });

    form.addEventListener('input', (event) => {
      if (event.target.matches('input, select, textarea') && event.target.checkValidity()) {
        event.target.setAttribute('aria-invalid', 'false');
      }
    });

    form.addEventListener('invalid', (event) => {
      event.target.setAttribute('aria-invalid', 'true');
    }, true);

    form.addEventListener('submit', (event) => {
      const honeypot = form.querySelector('[data-eai-honeypot]');
      if (honeypot?.value) {
        event.preventDefault();
        return;
      }

      if (!form.checkValidity()) {
        event.preventDefault();
        const invalid = form.querySelector(':invalid');
        if (invalid) {
          invalid.setAttribute('aria-invalid', 'true');
          invalid.reportValidity();
          invalid.focus();
        }
        return;
      }

      const pending = savePendingSubmission(root, form, startedAt);
      pushEvent({
        event: 'enterprise_form_submit_attempt',
        form_name: formName(form),
        form_id: form.id,
        lead_id: pending.leadId,
        budget_band: pending.budgetBand,
        delivery_country: pending.country,
        project_type: pending.projectType,
        optional_details_opened: pending.optionalDetails,
        completion_seconds: pending.completionSeconds,
        section_id: root.dataset.eaiSection || ''
      });
      const button = form.querySelector('[data-eai-submit]');
      if (button) {
        button.disabled = true;
        button.textContent = button.dataset.eaiSendingLabel || 'Sending request…';
      }
    });

    handleServerResult(root, form);
  }

  function initHeroTracking() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-eai-hero-cta]');
      if (!link) return;
      pushEvent({
        event: 'enterprise_hero_cta_click',
        cta_type: link.dataset.eaiHeroCta || '',
        cta_label: String(link.textContent || '').trim(),
        destination: link.getAttribute('href') || ''
      });
    });
  }

  function initExploreNavigation() {
    const nav = document.querySelector('[data-eai-explore]');
    if (!nav || nav.dataset.eaiExploreReady === 'true') return;

    const links = Array.from(nav.querySelectorAll('[data-eai-nav-link]'));
    const items = links.map((link) => {
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('#') || href.length < 2) return null;
      return { link, href, target: document.getElementById(href.slice(1)) };
    }).filter((item) => item?.target);
    if (!items.length) return;

    nav.dataset.eaiExploreReady = 'true';
    const currentLabel = nav.querySelector('[data-eai-current-label]');
    const linksViewport = nav.querySelector('.eai-jump__links');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const topNavigation = window.matchMedia('(max-width: 989px)');
    const pageBoundary = document.getElementById('MainContent');
    let activeHref = '';
    let scheduled = false;

    const visibleHeaderBottom = () => {
      const candidates = Array.from(document.querySelectorAll('sticky-header, .section-header'));
      return candidates.reduce((bottom, element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= 220 || rect.height > 260) return bottom;
        return Math.max(bottom, Math.min(rect.bottom, 220));
      }, 0);
    };

    const positionNavigation = () => {
      const top = Math.max(0, Math.round(visibleHeaderBottom()));
      nav.style.setProperty('--eai-explore-top', `${top}px`);

      const contentBottom = pageBoundary
        ? pageBoundary.getBoundingClientRect().bottom
        : Number.POSITIVE_INFINITY;

      if (topNavigation.matches) {
        nav.style.removeProperty('--eai-explore-bottom');
        nav.classList.toggle('has-reached-footer', contentBottom <= top + nav.offsetHeight + 8);
      } else {
        nav.style.removeProperty('--eai-explore-bottom');
        nav.classList.remove('has-reached-footer');
      }

      const scrollOffset = topNavigation.matches ? top + nav.offsetHeight + 14 : top + 14;
      document.documentElement.style.setProperty('--eai-scroll-offset', `${scrollOffset}px`);
      return scrollOffset;
    };

    const setActive = (item) => {
      if (!item || item.href === activeHref) return;
      activeHref = item.href;
      items.forEach((candidate) => {
        const active = candidate === item;
        candidate.link.classList.toggle('is-active', active);
        if (active) candidate.link.setAttribute('aria-current', 'location');
        else candidate.link.removeAttribute('aria-current');
      });
      if (currentLabel) currentLabel.textContent = item.link.dataset.eaiNavLabel || '';
      if (linksViewport && topNavigation.matches) {
        const linkRect = item.link.getBoundingClientRect();
        const viewportRect = linksViewport.getBoundingClientRect();
        const destination = linksViewport.scrollLeft
          + linkRect.left
          - viewportRect.left
          - (viewportRect.width - linkRect.width) / 2;
        linksViewport.scrollTo({ left: destination, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    };

    const updateActiveSection = () => {
      const offset = positionNavigation();
      const marker = topNavigation.matches
        ? offset + Math.min(180, window.innerHeight * 0.22)
        : Math.min(300, window.innerHeight * 0.34);
      let current = items[0];
      items.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= marker) current = item;
      });
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        current = items[items.length - 1];
      }
      setActive(current);
    };

    const scheduleUpdate = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        updateActiveSection();
      });
    };

    items.forEach((item) => {
      item.link.addEventListener('click', (event) => {
        event.preventDefault();
        const offset = positionNavigation();
        const destination = Math.max(0, window.scrollY + item.target.getBoundingClientRect().top - offset);
        setActive(item);
        window.history.replaceState(null, '', item.href);
        window.scrollTo({ top: destination, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        pushEvent({
          event: 'enterprise_explore_click',
          section_label: item.link.dataset.eaiNavLabel || '',
          section_anchor: item.href
        });
      });
    });

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate, { once: true });
    topNavigation.addEventListener?.('change', scheduleUpdate);
    updateActiveSection();
  }

  function initializePage() {
    initHeroTracking();
    initExploreNavigation();
    document.querySelectorAll('[data-eai-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePage, { once: true });
  } else {
    initializePage();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initExploreNavigation();
    if (event.target.matches?.('[data-eai-section]')) initSection(event.target);
    event.target.querySelectorAll?.('[data-eai-section]').forEach(initSection);
  });
})();
